/** Where the load of a part of the editor stands, for every editor on the page: it is loaded once. */
export type ModuleStatus<M> =
  | { phase: "idle" | "loading"; module: null; error: null }
  | { phase: "ready"; module: M; error: null }
  | { phase: "failed"; module: null; error: Error };

/** What a host may watch: where the load stands, and nothing that starts it. */
export interface ModuleSupport<M> {
  subscribe(listener: () => void): () => void;
  getPhase(): ModuleStatus<M>["phase"];
}

export interface ModuleLoader<M> {
  /** Starts the load if nothing has, and again after a failure. Safe to call on every render. */
  load(): void;
  /** Resolves when editors can use the module. */
  preload(): Promise<void>;
  /** Shaped for `useSyncExternalStore`. Asking never starts the load. */
  subscribe(listener: () => void): () => void;
  get(): ModuleStatus<M>;
}

/**
 * A part of the editor that is fetched when it is needed and kept for the life of the page. The
 * dynamic `import()` stays in the caller, written out, so that a bundler sees where the chunk is.
 */
export function createModuleLoader<M>(fetch: () => Promise<M>): ModuleLoader<M> {
  let status: ModuleStatus<M> = { phase: "idle", module: null, error: null };
  const listeners = new Set<() => void>();
  const set = (next: ModuleStatus<M>): void => {
    status = next;
    for (const listener of listeners) listener();
  };
  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };
  const load = (): void => {
    if (status.phase === "loading" || status.phase === "ready") return;
    set({ phase: "loading", module: null, error: null });
    fetch().then(
      (module) => set({ phase: "ready", module, error: null }),
      (error: unknown) =>
        set({
          phase: "failed",
          module: null,
          error: error instanceof Error ? error : new Error(String(error)),
        }),
    );
  };
  return {
    load,
    subscribe,
    get: () => status,
    preload() {
      load();
      return new Promise((resolve, reject) => {
        const settle = (): boolean => {
          if (status.phase === "ready") resolve();
          else if (status.phase === "failed") reject(status.error);
          else return false;
          return true;
        };
        if (settle()) return;
        const off = subscribe(() => {
          if (settle()) off();
        });
      });
    },
  };
}
